import { useCallback, useEffect } from 'react';
import { useAuth, useLazyCommand } from '@soap/modules';
import { useSessionDebugMode } from './useSessionDebugMode';

/** Sends session debug mode to the server for the current SignalR connection. */
export function useSyncLywSessionDebugMode() {
  const [setLywSessionDebugMode] = useLazyCommand('C439v1_SetLywSessionDebugMode');

  return useCallback(
    async (enabled: boolean) => {
      try {
        await setLywSessionDebugMode({ C439_Enabled: enabled });
      } catch {
        // Best-effort sync; UI still follows tab sessionStorage.
      }
    },
    [setLywSessionDebugMode]
  );
}

/**
 * Keeps server SessionDebugModeRegistry aligned with tab sessionStorage.
 * Required so E438 AI activity events publish after reload or SignalR reconnect.
 */
export function useEnsureLywSessionDebugModeSynced() {
  const { authReady, isAuthenticated } = useAuth('useEnsureLywSessionDebugModeSynced');
  const sessionDebugMode = useSessionDebugMode();
  const syncSessionDebugMode = useSyncLywSessionDebugMode();

  useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    void syncSessionDebugMode(sessionDebugMode);
  }, [authReady, isAuthenticated, sessionDebugMode, syncSessionDebugMode]);

  // SignalR may reconnect with a new connection id while sessionStorage stays enabled.
  useEffect(() => {
    if (!authReady || !isAuthenticated || !sessionDebugMode) return;

    const resync = () => {
      void syncSessionDebugMode(true);
    };

    window.addEventListener('focus', resync);
    return () => window.removeEventListener('focus', resync);
  }, [authReady, isAuthenticated, sessionDebugMode, syncSessionDebugMode]);
}
